'use client'
import {
  extractMatchData
} from "./chunk-52IPYQXN.mjs";
import {
  DecoratedSection_default
} from "./chunk-HJCFOVNG.mjs";

// src/components/round-robin/RoundRobinOrganization.tsx
import { Fragment, useMemo, useState } from "react";
import { useEventsList, useGetOrganizationBySlug } from "@wbk/api/hooks";
import { useRouter } from "@wbk/config";
import { ImageWithPlaceholder, Paper, ToggleGroup, Typography as Typography2 } from "@wbk/ui";

// src/components/round-robin/Cell.tsx
import { Typography } from "@wbk/ui";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
var ScoreCell = ({ rowScore, colScore }) => {
  const rowWins = rowScore > colScore;
  const colWins = colScore > rowScore;
  return /* @__PURE__ */ jsxs("div", { className: "py-M flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(
      Typography,
      {
        as: "div",
        variant: "label-L",
        className: twMerge(
          "flex h-10 w-10 items-center justify-center rounded-full",
          rowWins && "bg-success text-primary-contrast"
        ),
        children: rowScore
      }
    ),
    /* @__PURE__ */ jsx(Typography, { variant: "label-L", children: "-" }),
    /* @__PURE__ */ jsx(
      Typography,
      {
        as: "div",
        variant: "label-L",
        className: twMerge(
          "flex h-10 w-10 items-center justify-center rounded-full",
          colWins && "bg-success text-primary-contrast"
        ),
        children: colScore
      }
    )
  ] });
};
var Cell_default = ScoreCell;

// src/components/round-robin/RoundRobinOrganization.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var RoundRobinOrganization = ({ section }) => {
  var _a;
  const { locale } = useRouter();
  const organizationSlug = section.organizationSlug;
  const {
    data: orgData,
    isLoading: orgLoading,
    error: orgError
  } = useGetOrganizationBySlug(
    { lang: locale, slug: organizationSlug },
    { enabled: !!organizationSlug }
  );
  const {
    data: eventsData,
    isLoading: eventsLoading,
    error: eventsError
  } = useEventsList(
    { lang: locale, publicOnly: true, organizationSlug: organizationSlug != null ? organizationSlug : "" },
    { enabled: !!organizationSlug }
  );
  const roundRobinGroups = orgData == null ? void 0 : orgData.event_groups;
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const activeGroupId = selectedGroupId || ((_a = roundRobinGroups == null ? void 0 : roundRobinGroups[0]) == null ? void 0 : _a._id) || "";
  const { teams, matchMap } = useMemo(() => {
    if (!(eventsData == null ? void 0 : eventsData.events) || !activeGroupId) return { teams: [], matchMap: /* @__PURE__ */ new Map() };
    const groupEvents = eventsData.events.filter((ev) => ev.event_group_id === activeGroupId);
    return extractMatchData(groupEvents);
  }, [eventsData, activeGroupId]);
  if (orgError || eventsError || !organizationSlug) return null;
  if (!orgLoading && !eventsLoading && !(roundRobinGroups == null ? void 0 : roundRobinGroups.length) && !roundRobinGroups) return null;
  const isLoading = orgLoading || eventsLoading;
  return /* @__PURE__ */ jsxs2(
    DecoratedSection_default,
    {
      section,
      className: "container space-y-6",
      "data-testid": `section_round_robin_organization_${section.id}`,
      children: [
        /* @__PURE__ */ jsxs2("div", { className: "gap-M flex flex-wrap items-center justify-between", children: [
          section.title && /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx2(Typography2, { as: "h2", variant: "heading-L", children: section.title }),
            section.subtitle && /* @__PURE__ */ jsx2(Typography2, { color: "text-secondary", className: "mt-1", children: section.subtitle })
          ] }),
          (roundRobinGroups == null ? void 0 : roundRobinGroups.length) && roundRobinGroups.length > 1 && /* @__PURE__ */ jsx2(
            ToggleGroup,
            {
              type: "single",
              value: activeGroupId,
              onChange: setSelectedGroupId,
              options: roundRobinGroups.map((g) => ({ text: g.title, value: g._id })),
              groupClassName: "h-12 p-1 bg-body-level-2",
              className: "px-M h-full",
              activeBtnProps: { theme: "action", shape: "outlined", className: "bg-body-level-1" },
              btnProps: {
                theme: "action",
                shape: "text"
              },
              phill: true
            }
          )
        ] }),
        isLoading ? /* @__PURE__ */ jsx2(
          "div",
          {
            className: "rounded-S grid gap-px overflow-hidden",
            style: {
              gridTemplateColumns: "minmax(12rem, auto) repeat(5, minmax(10rem, 1fr))"
            },
            children: Array.from({ length: 6 }).map(
              (_, row) => Array.from({ length: 6 }).map((_2, col) => /* @__PURE__ */ jsx2("div", { className: "bg-body-level-2 h-20 animate-pulse" }, `${row}-${col}`))
            )
          }
        ) : teams.length > 0 ? /* @__PURE__ */ jsx2(Paper, { className: "overflow-x-auto !p-0", children: /* @__PURE__ */ jsxs2(
          "div",
          {
            className: "grid gap-px",
            style: {
              gridTemplateColumns: `minmax(12rem, auto) repeat(${teams.length}, minmax(10rem, 1fr))`
            },
            children: [
              /* @__PURE__ */ jsx2("div", { className: "bg-body-level-1 rounded-tl-md p-3" }),
              teams.map((team) => /* @__PURE__ */ jsxs2("div", { className: "bg-body-level-1 space-y-S p-M gap-2", children: [
                /* @__PURE__ */ jsx2(
                  ImageWithPlaceholder,
                  {
                    src: team.logo,
                    alt: team.name,
                    className: "mx-auto h-12 w-12 shrink-0 rounded-full object-cover"
                  }
                ),
                /* @__PURE__ */ jsx2(Typography2, { variant: "label-L", className: "text-center", children: team.name })
              ] }, `col-${team._id}`)),
              teams.map((rowTeam, rowIdx) => /* @__PURE__ */ jsxs2(Fragment, { children: [
                /* @__PURE__ */ jsxs2("div", { className: "bg-body-level-1 space-y-S p-M gap-2", children: [
                  /* @__PURE__ */ jsx2(
                    ImageWithPlaceholder,
                    {
                      src: rowTeam.logo,
                      alt: rowTeam.name,
                      className: "mx-auto h-12 w-12 shrink-0 rounded-full object-cover"
                    }
                  ),
                  /* @__PURE__ */ jsx2(Typography2, { variant: "label-L", className: "text-center", children: rowTeam.name })
                ] }),
                teams.map((colTeam, colIdx) => {
                  const isDiagonal = rowIdx === colIdx;
                  if (isDiagonal) {
                    return /* @__PURE__ */ jsx2("div", { className: "bg-body-level-1" }, `cell-${rowTeam._id}-${colTeam._id}`);
                  }
                  const direct = matchMap.get(`${rowTeam._id}::${colTeam._id}`);
                  const inverse = matchMap.get(`${colTeam._id}::${rowTeam._id}`);
                  const match = direct ? { rowScore: direct.homeScore, colScore: direct.awayScore } : inverse ? { rowScore: inverse.awayScore, colScore: inverse.homeScore } : null;
                  return /* @__PURE__ */ jsx2(
                    "div",
                    {
                      className: "bg-body flex items-center justify-center gap-2 p-3",
                      children: match ? /* @__PURE__ */ jsx2(Cell_default, { rowScore: match.rowScore, colScore: match.colScore }) : /* @__PURE__ */ jsx2(Typography2, { variant: "label-L", children: "\u2014" })
                    },
                    `cell-${rowTeam._id}-${colTeam._id}`
                  );
                })
              ] }, `row-${rowTeam._id}`))
            ]
          }
        ) }) : null
      ]
    }
  );
};
var RoundRobinOrganization_default = RoundRobinOrganization;

export {
  RoundRobinOrganization_default
};
//# sourceMappingURL=chunk-OMWUQJ2O.mjs.map